import { PropsWithChildren, ReactElement } from "react";
import { convertToAttributes, generateOptionsString } from "src/components/criteria/criteria-settings";
import { Skeleton } from "src/components/ui/skeleton";
import { SurveyAudienceCriteria, TotalUsersStats } from "src/redux/survey/survey.types";
import ResultsSidebarDetailsTooltip from "./results-sidebar-details-tooltip";

type DetailsTitleProps =
  | {
    variant: "skeleton",
    skeletonTitle?: string,
    skeletonCount?: boolean,
  }
  | {
    variant: "title",
    title: string,
    value?: number | string,
  };

const DetailsTitle = (props: DetailsTitleProps): ReactElement => {
  const { variant } = props;

  if (variant === "skeleton") {
    const { skeletonTitle, skeletonCount } = props;

    const renderSkeletonTitle = () => {
      if (!skeletonTitle) {
        return <Skeleton className={`h-6 ${skeletonCount ? "w-2/3" : "w-full"}`} />;
      }

      return (
        <span>
          {skeletonTitle}
        </span>
      );
    };

    return (
      <div className="flex justify-between text-base font-medium">
        {renderSkeletonTitle()}
        {skeletonCount && <Skeleton className="h-6 w-8" />}
      </div>
    );
  }

  const { title, value } = props;

  return (
    <div className="flex justify-between text-base font-medium">
      <span>
        {title}
      </span>
      {value !== undefined && (
        <span>
          {value}
        </span>
      )}
    </div>
  );
};


const DetailsItem = ({ title, titleIcon, value, tooltip }: { title: string, titleIcon?: ReactElement, value: string, tooltip?: string }): ReactElement => {
  return (
    <div className="flex justify-between text-base gap-2">
      <div className="flex gap-2 items-center">
        {Boolean(titleIcon) && titleIcon}
        {title}
      </div>
      <div className="flex gap-1 text-right items-center">
        {value}
        <ResultsSidebarDetailsTooltip tooltip={tooltip} />
      </div>
    </div>
  );
};

const DetailsLayout = ({ children }: PropsWithChildren): ReactElement => {
  return (
    <div className="flex flex-col gap-2">
      {children}
    </div>
  );
};

type RespondentsDetailsProps = {
  /** New unified shape: total, started, completed (no ai/human split) */
  total_users?: TotalUsersStats;
  /** Legacy: used when total_users is not present */
  totalUsers?: number;
  alignmentScore?: number;
};

const ResultsSidebarDetailsRespondents = (props: RespondentsDetailsProps): ReactElement => {
  const { total_users, totalUsers, alignmentScore } = props;
  const total = total_users?.total ?? totalUsers ?? 0;
  const started = total_users?.started ?? 0;
  const completed = total_users?.completed ?? 0;
  const completionRate = started === 0 ? "(0)" : `${((completed / started) * 100).toFixed(1).replace(".0", "")}% (${completed})`;

  return (
    <DetailsLayout>
      <DetailsTitle
        variant="title"
        title="Total Respondents"
        value={total}
      />
      <DetailsTitle
        variant="title"
        title="Started"
        value={started}
      />
      <DetailsTitle
        variant="title"
        title="Completed"
        value={completed}
      />
      <DetailsTitle
        variant="title"
        title="Completion Rate"
        value={completionRate}
      />
      {alignmentScore !== undefined && (
        <>
          <hr className="my-2" />
          <DetailsItem
            title="Alignment"
            value={`${+alignmentScore.toFixed(1)}%`}
            tooltip="This score reflects the alignment percentage between AI panelists and human survey respondents."
          />
        </>
      )}
    </DetailsLayout>
  );
};

type ResultsSidebarDetailsAudienceProps = {
  criteria?: SurveyAudienceCriteria;
};

const ResultsSidebarDetailsAudience = ({ criteria }: ResultsSidebarDetailsAudienceProps): ReactElement | null => {
  if (!criteria) {
    return null;
  }

  return (
    <DetailsLayout>
      <DetailsTitle
        variant="title"
        title="Target Audience"
      />
      {convertToAttributes(criteria).map(crit => (
        <DetailsItem
          key={crit.value}
          title={crit.label}
          value={generateOptionsString(crit.options, crit.value)}
        />
      ))}
    </DetailsLayout>
  );
};

export { ResultsSidebarDetailsRespondents, ResultsSidebarDetailsAudience, DetailsItem, DetailsTitle, DetailsLayout };
